import React, { useState, useEffect, useRef } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from '../contexts/AuthContext';
import { 
  X, Save, FileText, Calendar, User, Users, Package, 
  Wrench, DollarSign, Search, Plus, Trash2, Upload, 
  Building, AlertCircle, Clock, CheckCircle, Phone, Mail
} from 'lucide-react';
import BlacklistAlert, { BlacklistWarningCard } from './BlacklistAlert';
import { 
  createOrder, 
  updateOrder, 
  getNextFolio, 
  validateFolioUnique, 
  getValidStatusOptions 
} from '../lib/orderService';
import { getBulkClientBlacklistStatus } from '../lib/blacklistService';
import ClientForm from './ClientForm';
import EquipmentForm from './EquipmentForm';

const OrderForm = ({ isOpen, onClose, onSuccess, editingOrder = null }) => {
  const { currentUser, userRole } = useAuth();
  const [activeTab, setActiveTab] = useState('basic');
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [saveStatus, setSaveStatus] = useState('');
  const [isAutoGenerated, setIsAutoGenerated] = useState(true);
  const [folioError, setFolioError] = useState('');
  
  // ClientForm modal state
  const [showClientForm, setShowClientForm] = useState(false);
  
  // Helper function to format date for input fields
  const formatDateForInput = (date) => {
    if (!date) return '';
    
    let dateObj;
    if (date.toDate) {
      // Firebase Timestamp
      dateObj = date.toDate();
    } else if (date instanceof Date) {
      dateObj = date;
    } else if (typeof date === 'string') {
      dateObj = new Date(date);
    } else {
      return '';
    }
    
    // Use local date components to avoid timezone issues
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    
    return `${year}-${month}-${day}`;
  };

  // Helper function to create Date object from date input string (YYYY-MM-DD) in local timezone
  const createLocalDate = (dateString) => {
    if (!dateString) return null;
    
    // Split the date string and create a Date object in local timezone
    const [year, month, day] = dateString.split('-').map(num => parseInt(num, 10));
    
    // Note: Month is 0-indexed in JavaScript Date constructor
    return new Date(year, month - 1, day);
  };

  // Form data state
  const [formData, setFormData] = useState({
    folio: '',
    fecha: formatDateForInput(new Date()),
    tipo_de_orden: 'Reparación',
    status_orden: 'Abierta',
    cliente: '',
    equipos: [],
    tecnicos: [],
    observaciones_detalle: '',
    fecha_conclusion_servicio: '',
    
    // Type-specific fields
    comentarios_cliente: '',
    resultados_revision: '',
    folio_cotizacion: '',
    importe: '',
    autorizacion: '',
    fecha_autorizacion: '',
    folio_orden_compra: '',
    folio_factura_contpaq: '',
    folio_salida_contpaq: '',
    estatus_pago: '',
    fecha_entrega_equipo: '',
    quien_recibe_equipo: '',
    
    // Warranty specific
    fecha_compra_equipo: '',
    folio_compra: '',
    rma: '',
    fecha_envio_fabricante: '',
    fecha_respuesta: '',
    veredicto: '',
    detalle_veredicto: ''
  });
  
  const [errors, setErrors] = useState({});
  
  // Data collections
  const [clients, setClients] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [brands, setBrands] = useState([]);
  const [specs, setSpecs] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [filteredEquipment, setFilteredEquipment] = useState([]);
  
  // Search states
  const [clientSearch, setClientSearch] = useState('');
  const [equipmentSearch, setEquipmentSearch] = useState('');
  const [showClientResults, setShowClientResults] = useState(false);
  const [showEquipmentResults, setShowEquipmentResults] = useState(false);
  
  // Equipment management
  const [newExemptEquipment, setNewExemptEquipment] = useState({
    descripcion: '',
    series: '',
    notes: ''
  });
  const [showExemptForm, setShowExemptForm] = useState(false);
  const [showEquipmentForm, setShowEquipmentForm] = useState(false);

  // Track if folio has been generated to prevent multiple calls
  const folioGeneratedRef = useRef(false);

  // Load data when form opens
  useEffect(() => {
    if (isOpen) {
      loadData();
      if (!editingOrder && !folioGeneratedRef.current && isAutoGenerated && (!formData.folio || formData.folio === '')) {
        folioGeneratedRef.current = true;
        generateFolio();
      }
    }
    return () => {
      // Reset on cleanup
      if (!isOpen) {
        folioGeneratedRef.current = false;
      }
    };
  }, [isOpen, isAutoGenerated]);

  // Populate form when editing
  useEffect(() => {
    if (editingOrder) {
      setFormData({
        ...editingOrder,
        fecha: formatDateForInput(editingOrder.fecha) || formatDateForInput(new Date()),
        fecha_conclusion_servicio: formatDateForInput(editingOrder.fecha_conclusion_servicio),
        fecha_autorizacion: formatDateForInput(editingOrder.fecha_autorizacion),
        fecha_entrega_equipo: formatDateForInput(editingOrder.fecha_entrega_equipo),
        fecha_compra_equipo: formatDateForInput(editingOrder.fecha_compra_equipo),
        fecha_envio_fabricante: formatDateForInput(editingOrder.fecha_envio_fabricante),
        fecha_respuesta: formatDateForInput(editingOrder.fecha_respuesta),
        equipos: editingOrder.equipos || [],
        tecnicos: editingOrder.tecnicos || [],
        // Handle conversion from old boolean autorizacion to new string format
        autorizacion: typeof editingOrder.autorizacion === 'boolean' 
          ? (editingOrder.autorizacion ? 'Autorizado' : '') 
          : (editingOrder.autorizacion || '')
      });
      setIsAutoGenerated(false);
    } else {
      // Reset form for new order
      setFormData({
        folio: '',
        fecha: formatDateForInput(new Date()),
        tipo_de_orden: 'Reparación',
        status_orden: 'Abierta',
        cliente: '',
        equipos: [],
        tecnicos: [],
        observaciones_detalle: '',
        fecha_conclusion_servicio: '',
        comentarios_cliente: '',
        resultados_revision: '',
        folio_cotizacion: '',
        importe: '',
        autorizacion: '',
        fecha_autorizacion: '',
        folio_orden_compra: '',
        folio_factura_contpaq: '',
        folio_salida_contpaq: '',
        estatus_pago: '',
        fecha_entrega_equipo: '',
        quien_recibe_equipo: '',
        fecha_compra_equipo: '',
        folio_compra: '',
        rma: '',
        fecha_envio_fabricante: '',
        fecha_respuesta: '',
        veredicto: '',
        detalle_veredicto: ''
      });
      setIsAutoGenerated(true);
    }
    setErrors({});
    setSubmitError('');
  }, [editingOrder, isOpen]);

  const loadData = async () => {
    try {
      await Promise.all([
        fetchClients(),
        fetchTechnicians(),
        fetchEquipment(),
        fetchSpecs()
      ]);
    } catch (error) {
      console.error('Error loading data:', error);
    }
  };

  const fetchClients = async () => {
    try {
      const q = query(collection(db, 'clients'), where('isArchived', '==', false));
      const querySnapshot = await getDocs(q);
      let clientsList = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Load blacklist status for all clients
      try {
        const clientIds = clientsList.map(client => client.id);
        const blacklistStatus = await getBulkClientBlacklistStatus(clientIds);
        
        // Add blacklist status to clients
        clientsList = clientsList.map(client => ({
          ...client,
          isBlacklisted: blacklistStatus[client.id]?.isBlacklisted || false,
          activeIncidentsCount: blacklistStatus[client.id]?.activeIncidentsCount || 0,
          mostRecentIncidentMotivo: blacklistStatus[client.id]?.mostRecentIncidentMotivo || null
        }));
        
      } catch (error) {
        console.error('Error loading blacklist status:', error);
        // Continue without blacklist status if it fails
      }
      
      setClients(clientsList);
    } catch (error) {
      console.error('Error fetching clients:', error);
      setClients([]);
    }
  };

  const fetchTechnicians = async () => {
    try {
      const q = query(collection(db, 'technicians'), where('isArchived', '==', false));
      const querySnapshot = await getDocs(q);
      const techniciansList = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setTechnicians(techniciansList);
    } catch (error) {
      console.error('Error fetching technicians:', error);
      setTechnicians([]);
    }
  };

  const fetchEquipment = async (forceRefresh = false) => {
    try {
      if (forceRefresh) {
        // Force refresh from Firestore
        const { refreshEquipmentData } = await import('../lib/equipmentService');
        const equipmentList = await refreshEquipmentData();
        setEquipment(equipmentList);
      } else {
        // Use the cached equipment service to get equipment with populated brand/type names
        const { getAllEquipment } = await import('../lib/equipmentService');
        const equipmentList = await getAllEquipment();
        setEquipment(equipmentList);
      }
      
      // Also fetch brands for search functionality
      const { getAllBrands } = await import('../lib/brandService');
      const brandsList = await getAllBrands();
      setBrands(brandsList);
    } catch (error) {
      console.error('Error fetching equipment:', error);
      setEquipment([]);
    }
  };

  const fetchSpecs = async () => {
    try {
      const q = query(collection(db, 'specs'));
      const querySnapshot = await getDocs(q);
      const specsList = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      // Sort by priority (lower priority number = higher priority)
      const sortedSpecs = specsList.sort((a, b) => (a.prioridad || 999) - (b.prioridad || 999));
      setSpecs(sortedSpecs);
    } catch (error) {
      console.error('Error fetching specs:', error);
      setSpecs([]);
    }
  };

  const generateFolio = async () => {
    // Don't generate if user has already entered a folio manually
    if (!isAutoGenerated && formData.folio && formData.folio !== '') {
      return;
    }
    
    try {
      const nextFolio = await getNextFolio();
      setFormData(prev => ({ ...prev, folio: nextFolio }));
      setIsAutoGenerated(true);
    } catch (error) {
      console.error('Error generating folio:', error);
      // Set folio to empty so user must enter manually
      setFormData(prev => ({ ...prev, folio: '' }));
      setIsAutoGenerated(false);
      setSubmitError(error.message || 'No se puede generar el folio automáticamente. Por favor, ingrese el siguiente número secuencial manualmente.');
    }
  };

  const handleFolioChange = async (e) => {
    const inputValue = e.target.value;
    const newFolio = inputValue === '' ? '' : (parseInt(inputValue) || '');
    setFormData(prev => ({ ...prev, folio: newFolio }));
    setIsAutoGenerated(false);
    
    // Clear any previous folio errors
    setFolioError('');
    
    // Clear the main error message when user starts typing
    if (submitError && submitError.includes('folio')) {
      setSubmitError('');
    }
    
    // Validate folio uniqueness if we have a valid number and it's for admin
    if (newFolio && userRole === 'admin') {
      try {
        const isUnique = await validateFolioUnique(newFolio, editingOrder?.id);
        if (!isUnique) {
          setFolioError('Este folio ya existe');
        } else {
          setFolioError('');
        }
      } catch (error) {
        // Ignore validation errors if collections don't exist yet
        console.log('Folio validation skipped - collections may not exist yet');
      }
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear related errors
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
    
    // Handle order type change
    if (field === 'tipo_de_orden') {
      const statusOptions = getValidStatusOptions(value);
      setFormData(prev => ({ 
        ...prev, 
        [field]: value,
        status_orden: statusOptions[0] || 'Abierta'
      }));
      
      // Clear client for Fabricación orders
      if (value === 'Fabricación') {
        setFormData(prev => ({ ...prev, cliente: '' }));
      }
    }

    // Handle authorization status change
    if (field === 'autorizacion') {
      // Clear authorization date if status is not "Autorizado"
      if (value !== 'Autorizado') {
        setFormData(prev => ({ 
          ...prev, 
          [field]: value,
          fecha_autorizacion: ''
        }));
      } else {
        setFormData(prev => ({ ...prev, [field]: value }));
      }
      
      // Clear related errors
      if (errors.fecha_autorizacion) {
        setErrors(prev => ({ ...prev, fecha_autorizacion: '' }));
      }
      
      return; // Exit early to avoid the default setFormData call
    }

    // Handle guarantee verdict change
    if (field === 'veredicto') {
      if (value === 'No procede garantía') {
        const standardText = "El daño en el equipo es indicador de trabajo con lubricación insuficiente y exceso de arena. Los componentes no se encuentran en un estado reparable, por lo cual es necesario reemplazar el equipo. Este tipo de daño se considera resultado de condiciones de uso no adecuadas y no está cubierto por la garantía, la cual aplica únicamente para defectos de fabricación o fallos en condiciones normales de operación.";
        
        setFormData(prev => {
          const currentDetail = prev.detalle_veredicto || '';
          // Check if the standard text is already present to avoid duplication
          const hasStandardText = currentDetail.includes("El daño en el equipo es indicador de trabajo con lubricación insuficiente");
          
          if (!hasStandardText) {
            const newDetail = currentDetail.trim() 
              ? `${currentDetail}\n\n${standardText}`
              : standardText;
              
            return {
              ...prev,
              [field]: value,
              detalle_veredicto: newDetail
            };
          }
          
          return {
            ...prev,
            [field]: value
          };
        });
      } else if (value === 'Sin falla detectada') {
        const standardText = "Durante las pruebas realizadas en nuestras instalaciones, el equipo funcionó correctamente y no se detectó ninguna falla o anomalía en su operación.";
        
        setFormData(prev => {
          const currentDetail = prev.detalle_veredicto || '';
          // Check if the standard text is already present to avoid duplication
          const hasStandardText = currentDetail.includes("Durante las pruebas realizadas en nuestras instalaciones");
          
          if (!hasStandardText) {
            const newDetail = currentDetail.trim() 
              ? `${currentDetail}\n\n${standardText}`
              : standardText;
              
            return {
              ...prev,
              [field]: value,
              detalle_veredicto: newDetail
            };
          }
          
          return {
            ...prev,
            [field]: value
          };
        });
      } else {
        // For other verdicts, just update the field normally
        setFormData(prev => ({
          ...prev,
          [field]: value
        }));
      }
    }
  };

  // Handler for importe input to limit to 2 decimal places
  const handleImporteChange = (value) => {
    // Allow empty string
    if (value === '') {
      handleInputChange('importe', '');
      return;
    }
    
    // Validate and format the input to max 2 decimal places
    const regex = /^\d*\.?\d{0,2}$/;
    if (regex.test(value)) {
      handleInputChange('importe', value);
    }
  };

  // Helper function to normalize text for search (remove accents)
  const normalizeText = (text) => {
    if (!text) return '';
    return text.toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, ''); // Remove diacritical marks
  };

  // Client search functionality
  const handleClientSearch = (searchTerm) => {
    setClientSearch(searchTerm);
    if (searchTerm.trim()) {
      const normalizedSearchTerm = normalizeText(searchTerm);
      const filtered = clients.filter(client => 
        normalizeText(client.nombreEmpresa).includes(normalizedSearchTerm) ||
        (client.contactos || []).some(contact => 
          normalizeText(contact.nombre).includes(normalizedSearchTerm) ||
          contact.telefono?.includes(searchTerm) ||
          normalizeText(contact.email).includes(normalizedSearchTerm)
        )
      );
      setFilteredClients(filtered);
      setShowClientResults(true);
    } else {
      setShowClientResults(false);
    }
  };

  const selectClient = (client) => {
    setFormData(prev => ({ ...prev, cliente: client.id }));
    setClientSearch(client.nombreEmpresa || 'Cliente seleccionado');
    setShowClientResults(false);
  };

  // Client creation handlers
  const handleCreateNewClient = () => {
    setShowClientForm(true);
    setShowClientResults(false);
  };

  const handleClientFormClose = () => {
    setShowClientForm(false);
  };

  const handleClientFormSuccess = async (newClient) => {
    // Refresh the clients list
    try {
      const clientsCollection = collection(db, 'clients');
      const clientsQuery = query(clientsCollection, where('isArchived', '==', false));
      const clientsSnapshot = await getDocs(clientsQuery);
      const refreshedClients = clientsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setClients(refreshedClients);
      
      // Auto-select the newly created client
      setFormData(prev => ({ ...prev, cliente: newClient.id }));
      setClientSearch(newClient.nombreEmpresa || 'Cliente seleccionado');
      
      // Close the client form
      setShowClientForm(false);
    } catch (error) {
      console.error('Error refreshing clients after creation:', error);
    }
  };

  // Equipment search functionality
  const handleEquipmentSearch = (searchTerm) => {
    setEquipmentSearch(searchTerm);
    if (searchTerm.trim()) {
      const filtered = equipment.filter(equip => {
        const searchLower = searchTerm.toLowerCase();
        
        // Search in basic fields
        if (equip.modelo?.toLowerCase().includes(searchLower) ||
            equip.numeroParte?.toLowerCase().includes(searchLower) ||
            (equip.partesAlternativas || []).some(part => 
              part?.toLowerCase().includes(searchLower)
            )) {
          return true;
        }
        
        // Search in brand name using dynamic lookup
        if (equip.marcaId) {
          const brand = brands.find(b => b.id === equip.marcaId);
          if (brand && brand.nombre && brand.nombre.toLowerCase().includes(searchLower)) {
            return true;
          }
        }
        
        return false;
      });
      setFilteredEquipment(filtered);
      setShowEquipmentResults(true);
    } else {
      setShowEquipmentResults(false);
    }
  };

  const addCatalogEquipment = (equipmentItem) => {
    // Initialize custom specs for customizable specifications
    const customSpecs = {};
    if (equipmentItem.esCustomizable && equipmentItem.especificaciones) {
      Object.keys(equipmentItem.esCustomizable).forEach(specName => {
        if (equipmentItem.esCustomizable[specName]) {
          // Initialize with default value from catalog or empty string
          customSpecs[specName] = equipmentItem.especificaciones[specName] || '';
        }
      });
    }

    const newEquipment = {
      id: Date.now().toString(),
      catalogId: equipmentItem.id,
      type: 'catalog',
      series: '',
      notes: '',
      photos: [],
      customSpecs: customSpecs
    };
    
    setFormData(prev => ({
      ...prev,
      equipos: [...prev.equipos, newEquipment]
    }));
    
    setEquipmentSearch('');
    setShowEquipmentResults(false);
  };

  const addExemptEquipment = () => {
    if (!newExemptEquipment.descripcion.trim()) return;
    
    const newEquipment = {
      id: Date.now().toString(),
      type: 'exempt',
      descripcion: newExemptEquipment.descripcion,
      series: newExemptEquipment.series,
      notes: newExemptEquipment.notes,
      photos: []
    };
    
    setFormData(prev => ({
      ...prev,
      equipos: [...prev.equipos, newEquipment]
    }));
    
    setNewExemptEquipment({ descripcion: '', series: '', notes: '' });
    setShowExemptForm(false);
  };

  const removeEquipment = (equipmentId) => {
    setFormData(prev => ({
      ...prev,
      equipos: prev.equipos.filter(eq => eq.id !== equipmentId)
    }));
  };

  const updateEquipmentField = (equipmentId, field, value) => {
    setFormData(prev => ({
      ...prev,
      equipos: prev.equipos.map(eq => 
        eq.id === equipmentId ? { ...eq, [field]: value } : eq
      )
    }));
  };

  const updateEquipmentCustomSpec = (equipmentId, specName, value) => {
    setFormData(prev => ({
      ...prev,
      equipos: prev.equipos.map(eq => 
        eq.id === equipmentId 
          ? { 
              ...eq, 
              customSpecs: { 
                ...eq.customSpecs, 
                [specName]: value 
              } 
            } 
          : eq
      )
    }));
  };

  // Equipment form handlers
  const handleEquipmentFormSuccess = async (newEquipment) => {
    setShowEquipmentForm(false);
    
    // Invalidate equipment cache and force refresh to get the latest data
    const { invalidateCache } = await import('../lib/cacheService');
    invalidateCache('equipment');
    
    // Force refresh equipment list with fresh data from Firestore
    await fetchEquipment(true).catch(console.error);
    
    if (newEquipment && newEquipment.id) {
      // Add the new equipment to local equipment list to ensure it's available for display
      setEquipment(prev => {
        // Check if equipment already exists (it should after the refresh)
        const existingIndex = prev.findIndex(eq => eq.id === newEquipment.id);
        if (existingIndex >= 0) {
          // Equipment already exists from refresh, no need to add again
          return prev;
        } else {
          // Add new equipment just in case it wasn't in the refresh
          return [...prev, newEquipment];
        }
      });
      
      // If we have brand information, make sure it's in the brands list
      if (newEquipment.marcaId && newEquipment.marca) {
        setBrands(prev => {
          const existingBrand = prev.find(b => b.id === newEquipment.marcaId);
          if (!existingBrand) {
            return [...prev, { id: newEquipment.marcaId, nombre: newEquipment.marca }];
          }
          return prev;
        });
      }
      
      // Automatically add the newly created equipment to the order
      // Initialize custom specs for customizable specifications
      const customSpecs = {};
      if (newEquipment.esCustomizable && newEquipment.especificaciones) {
        Object.keys(newEquipment.esCustomizable).forEach(specName => {
          if (newEquipment.esCustomizable[specName]) {
            // Initialize with default value from catalog or empty string
            customSpecs[specName] = newEquipment.especificaciones[specName] || '';
          }
        });
      }

      const orderEquipment = {
        id: Date.now().toString(),
        catalogId: newEquipment.id,
        type: 'catalog',
        series: '',
        notes: '',
        photos: [],
        customSpecs: customSpecs
      };
      
      setFormData(prev => ({
        ...prev,
        equipos: [...prev.equipos, orderEquipment]
      }));
    }
  };

  const handleCloseEquipmentForm = () => {
    setShowEquipmentForm(false);
  };

  // Technician assignment
  const toggleTechnician = (technicianId) => {
    setFormData(prev => ({
      ...prev,
      tecnicos: prev.tecnicos.includes(technicianId)
        ? prev.tecnicos.filter(id => id !== technicianId)
        : [...prev.tecnicos, technicianId]
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.folio) {
      newErrors.folio = 'El folio es requerido';
    }
    
    if (!formData.fecha) {
      newErrors.fecha = 'La fecha es requerida';
    }
    
    if (folioError) {
      newErrors.folio = folioError;
    }
    
    // Validate authorization date for non-Fabricación orders
    if (['Reparación', 'Ensamble', 'Garantía'].includes(formData.tipo_de_orden)) {
      if (formData.autorizacion === 'Autorizado' && !formData.fecha_autorizacion) {
        newErrors.fecha_autorizacion = 'La fecha de autorización es requerida cuando está autorizado';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async (closeAfter = false) => {
    if (!validateForm()) return;
    
    try {
      setLoading(true);
      setSaveStatus('Guardando...');
      
      const orderData = {
        ...formData,
        fecha: createLocalDate(formData.fecha),
        fecha_conclusion_servicio: createLocalDate(formData.fecha_conclusion_servicio),
        fecha_autorizacion: createLocalDate(formData.fecha_autorizacion),
        fecha_entrega_equipo: createLocalDate(formData.fecha_entrega_equipo),
        fecha_compra_equipo: createLocalDate(formData.fecha_compra_equipo),
        fecha_envio_fabricante: createLocalDate(formData.fecha_envio_fabricante),
        fecha_respuesta: createLocalDate(formData.fecha_respuesta),
        importe: formData.importe ? parseFloat(formData.importe) : null
      };
      
      let result;
      if (editingOrder) {
        result = await updateOrder(editingOrder.id, orderData);
      } else {
        result = await createOrder(orderData);
      }
      
      setSaveStatus('Guardado exitosamente');
      setTimeout(() => setSaveStatus(''), 3000);
      
      if (closeAfter) {
        onSuccess(result);
        onClose();
      }
    } catch (error) {
      console.error('Error saving order:', error);
      setSubmitError(error.message || 'Error al guardar la orden');
    } finally {
      setLoading(false);
    }
  };

  const getTabsForOrderType = () => {
    const baseTabs = [
      { id: 'basic', label: 'Información Básica', icon: FileText },
      { id: 'equipment', label: 'Equipos', icon: Package },
      { id: 'technicians', label: 'Técnicos', icon: Wrench },
      { id: 'service', label: 'Detalles del Servicio', icon: FileText }
    ];
    
    if (formData.tipo_de_orden !== 'Fabricación') {
      baseTabs.splice(1, 0, { id: 'client', label: 'Cliente', icon: Building });
    }
    
    if (['Reparación', 'Ensamble', 'Garantía'].includes(formData.tipo_de_orden)) {
      baseTabs.push({ id: 'financial', label: 'Datos Administrativos', icon: DollarSign });
    }
    
    return baseTabs;
  };

  const getSelectedClientName = () => {
    if (!formData.cliente) return '';
    const client = clients.find(c => c.id === formData.cliente);
    return client?.nombreEmpresa || 'Cliente seleccionado';
  };

  const getSelectedClient = () => {
    if (!formData.cliente) return null;
    return clients.find(c => c.id === formData.cliente);
  };

  const getPrincipalContact = (contactos) => {
    if (!contactos || contactos.length === 0) return null;
    const principal = contactos.find(contact => contact.esPrincipal === true);
    return principal || contactos[0];
  };

  const getEquipmentDisplayName = (equip) => {
    if (equip.type === 'catalog') {
      const catalogItem = equipment.find(e => e.id === equip.catalogId);
      if (catalogItem) {
        const parts = [];
        
        // Add type if available (either direct field or from ID)
        if (catalogItem.tipo) {
          parts.push(catalogItem.tipo);
        }
        
        // Add brand if available (either direct field or lookup from ID)
        if (catalogItem.marca) {
          parts.push(catalogItem.marca);
        } else if (catalogItem.marcaId) {
          const brand = brands.find(b => b.id === catalogItem.marcaId);
          if (brand && brand.nombre) {
            parts.push(brand.nombre);
          }
        }
        
        // Add model if available
        if (catalogItem.modelo) parts.push(catalogItem.modelo);
        
        // Add part number in parentheses if available
        if (catalogItem.numeroParte) {
          parts.push(`(${catalogItem.numeroParte})`);
        }
        
        return parts.length > 0 ? parts.join(' ') : 'Equipo de catálogo';
      }
      
      // If catalogItem not found, equipment might still be loading
      return 'Cargando información del equipo...';
    }
    return equip.descripcion || 'Equipo exento';
  };

  const getCustomizableSpecs = (equip) => {
    if (equip.type !== 'catalog') return [];
    
    const catalogItem = equipment.find(e => e.id === equip.catalogId);
    if (!catalogItem || !catalogItem.esCustomizable || !catalogItem.especificaciones) {
      return [];
    }

    // Get customizable spec names
    const customizableSpecNames = Object.keys(catalogItem.esCustomizable)
      .filter(specName => catalogItem.esCustomizable[specName]);

    if (customizableSpecNames.length === 0) return [];

    // Create a map of spec names to their priority for quick lookup
    const specPriorityMap = {};
    specs.forEach(spec => {
      specPriorityMap[spec.nombre] = spec.prioridad || 999;
    });

    // Sort customizable specs by priority and return with default values
    return customizableSpecNames
      .sort((a, b) => {
        const priorityA = specPriorityMap[a] || 999;
        const priorityB = specPriorityMap[b] || 999;
        return priorityA - priorityB; // Lower priority number = higher priority
      })
      .map(specName => ({
        name: specName,
        defaultValue: catalogItem.especificaciones[specName] || ''
      }));
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-gray-500 dark:bg-gray-900 bg-opacity-75 dark:bg-opacity-75 flex items-center justify-center z-50">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-5xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              {editingOrder ? 'Editar Orden' : 'Nueva Orden'}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
          
          {saveStatus && (
            <div className="mt-2 text-sm text-green-600 dark:text-green-400">
              {saveStatus}
            </div>
          )}
        </div>

        {/* Error Message */}
        {submitError && (
          <div className="mx-6 mt-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 px-4 py-3 rounded-md">
            {submitError}
          </div>
        )}

        {/* Tabs */}
        <div className="border-b border-gray-200 dark:border-gray-700">
          <div className="px-4 sm:px-6 overflow-x-auto scrollbar-hide">
            <nav className="flex space-x-1 sm:space-x-4 min-w-max" aria-label="Tabs">
              {getTabsForOrderType().map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`py-2 px-2 sm:px-3 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap flex-shrink-0 ${
                      activeTab === tab.id
                        ? 'border-blue-500 dark:border-blue-400 text-blue-600 dark:text-blue-400'
                        : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                    }`}
                  >
                    <div className="flex items-center space-x-1 sm:space-x-2">
                      <Icon className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
                      <span className="hidden md:inline">{tab.label}</span>
                      <span className="md:hidden">
                        {tab.label === 'Información Básica' ? 'Básica' :
                         tab.label === 'Detalles del Servicio' ? 'Servicio' :
                         tab.label === 'Datos Administrativos' ? 'Administrativos' :
                         tab.label === 'Técnicos' ? 'Técnicos' :
                         tab.label === 'Cliente' ? 'Cliente' :
                         tab.label === 'Equipos' ? 'Equipos' :
                         tab.label}
                      </span>
                    </div>
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        <div className="p-6 bg-white dark:bg-gray-800">
          {/* Basic Information Tab */}
          {activeTab === 'basic' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Folio Field */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Folio *
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      value={formData.folio || ''}
                      onChange={handleFolioChange}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 ${
                        !isAutoGenerated && !formData.folio
                          ? 'border-yellow-300 dark:border-yellow-600 focus:ring-yellow-500 dark:focus:ring-yellow-400 bg-yellow-50 dark:bg-yellow-900/20' 
                          : 'border-gray-300 dark:border-gray-600 focus:ring-blue-500 dark:focus:ring-blue-400'
                      } ${errors.folio ? 'border-red-300 dark:border-red-600' : ''}`}
                      min="1"
                      placeholder={!isAutoGenerated ? "Ingrese el siguiente número secuencial" : ""}
                    />
                    <span className="absolute right-3 top-2 text-xs text-gray-500">
                      {isAutoGenerated ? 'Auto-generado' : 'Manual'}
                    </span>
                  </div>
                  {!isAutoGenerated && !formData.folio && (
                    <p className="mt-1 text-sm text-yellow-600">
                      Ingrese manualmente el siguiente número secuencial de folio
                    </p>
                  )}
                  {errors.folio && (
                    <p className="mt-1 text-sm text-red-600">{errors.folio}</p>
                  )}
                </div>

                {/* Date Field */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Fecha *
                  </label>
                  <input
                    type="date"
                    value={formData.fecha}
                    onChange={(e) => handleInputChange('fecha', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 ${
                      errors.fecha ? 'border-red-300 dark:border-red-600' : 'border-gray-300 dark:border-gray-600'
                    }`}
                  />
                  {errors.fecha && (
                    <p className="mt-1 text-sm text-red-600">{errors.fecha}</p>
                  )}
                </div>

                {/* Order Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Tipo de Orden
                  </label>
                  <select
                    value={formData.tipo_de_orden}
                    onChange={(e) => handleInputChange('tipo_de_orden', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="Reparación">Reparación</option>
                    <option value="Ensamble">Ensamble</option>
                    <option value="Garantía">Garantía</option>
                    <option value="Fabricación">Fabricación</option>
                  </select>
                </div>

                {/* Status */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Estado
                  </label>
                  <select
                    value={formData.status_orden}
                    onChange={(e) => handleInputChange('status_orden', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    {getValidStatusOptions(formData.tipo_de_orden).map(status => (
                      <option key={status} value={status}>{status}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* Client Tab */}
          {activeTab === 'client' && formData.tipo_de_orden !== 'Fabricación' && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Buscar Cliente
                </label>
                <div className="relative">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="text"
                      value={formData.cliente ? getSelectedClientName() : clientSearch}
                      onChange={(e) => handleClientSearch(e.target.value)}
                      onFocus={() => !formData.cliente && setShowClientResults(true)}
                      placeholder="Buscar por nombre, contacto, teléfono..."
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                    />
                    {formData.cliente && (
                      <button
                        onClick={() => {
                          setFormData(prev => ({ ...prev, cliente: '' }));
                          setClientSearch('');
                        }}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                  
                  {showClientResults && !formData.cliente && (
                    <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-y-auto">
                      {filteredClients.length > 0 ? (
                        filteredClients.map(client => (
                          <button
                            key={client.id}
                            onClick={() => selectClient(client)}
                            className="w-full text-left px-4 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 border-b border-gray-100 dark:border-gray-700 last:border-b-0"
                          >
                            <div className="flex items-center space-x-2">
                              <BlacklistAlert client={client} size="sm" />
                              <div className="font-medium text-gray-900 dark:text-white">{client.nombreEmpresa}</div>
                            </div>
                            {client.contactos && client.contactos[0] && (
                              <div className="text-sm text-gray-600 dark:text-gray-400">
                                {client.contactos[0].nombre} - {client.contactos[0].telefono}
                              </div>
                            )}
                          </button>
                        ))
                      ) : clientSearch.trim() ? (
                        <div className="px-4 py-2 text-gray-500 dark:text-gray-400 text-center">
                          <div>No se encontraron clientes</div>
                          <button
                            onClick={handleCreateNewClient}
                            className="mt-2 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 text-sm"
                          >
                            + Crear nuevo cliente
                          </button>
                        </div>
                      ) : null}
                    </div>
                  )}
                </div>
              </div>

              {/* Client Information Display */}
              {formData.cliente ? (() => {
                const client = getSelectedClient();
                const principal = getPrincipalContact(client?.contactos);
                const allContacts = client?.contactos || [];
                
                return client ? (
                  <div className="bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                    <div className="flex items-center space-x-2 mb-3">
                      <Building className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                      <h4 className="text-sm font-medium text-gray-900 dark:text-white">Cliente Seleccionado</h4>
                    </div>
                    
                    {/* Blacklist Warning Card */}
                    <BlacklistWarningCard client={client} className="mb-4" />
                    
                    <div className="space-y-4">
                      <div>
                        <div className="flex items-center space-x-2">
                          <BlacklistAlert client={client} size="sm" />
                          <h4 className="font-medium text-gray-900 dark:text-white">
                            {client.nombreEmpresa || (principal?.nombre || 'Cliente sin nombre')}
                          </h4>
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {client.tipoCliente === 'empresa' ? 'Empresa' : 'Particular'}
                        </p>
                      </div>
                      
                      {/* All Contacts Section */}
                      {allContacts.length > 0 && (
                        <div className="space-y-3">
                          <div className="flex items-center space-x-2">
                            <Users className="h-4 w-4 text-gray-400" />
                            <span className="text-sm font-medium text-gray-900 dark:text-white">
                              Contactos ({allContacts.length})
                            </span>
                          </div>
                          
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {allContacts.map((contact, index) => (
                              <div key={index} className="bg-gray-50 dark:bg-gray-600 rounded-md p-3 space-y-2">
                                <div className="flex items-center space-x-2">
                                  <User className="h-3 w-3 text-gray-400" />
                                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                                    {contact.nombre}
                                  </span>
                                  {contact.esPrincipal && (
                                    <span className="text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-400 px-2 py-0.5 rounded">
                                      Principal
                                    </span>
                                  )}
                                </div>
                                
                                {contact.telefono && (
                                  <div className="flex items-center space-x-2">
                                    <Phone className="h-3 w-3 text-gray-400" />
                                    <span className="text-xs text-gray-600 dark:text-gray-400">
                                      {contact.telefono}
                                    </span>
                                  </div>
                                )}
                                
                                {contact.email && (
                                  <div className="flex items-center space-x-2">
                                    <Mail className="h-3 w-3 text-gray-400" />
                                    <span className="text-xs text-gray-600 dark:text-gray-400">
                                      {contact.email}
                                    </span>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {client.isBlacklisted && (
                        <div className="flex items-center space-x-2 p-2 bg-red-50 dark:bg-red-900/20 rounded-md">
                          <AlertCircle className="h-4 w-4 text-red-500 dark:text-red-400" />
                          <span className="text-sm text-red-700 dark:text-red-400 font-medium">Cliente en lista negra</span>
                        </div>
                      )}
                    </div>
                  </div>
                ) : null;
              })() : (
                /* Placeholder to maintain consistent modal height */
                <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 min-h-[280px]">
                  <div className="flex items-center justify-center h-full text-gray-400 dark:text-gray-500">
                    <div className="text-center">
                      <Building className="h-8 w-8 mx-auto mb-2" />
                      <p className="text-sm">Selecciona un cliente para ver su información</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Equipment Tab */}
          {activeTab === 'equipment' && (
            <div className="space-y-6">
              {/* Equipment Search */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Buscar Equipo de Catálogo
                </label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    value={equipmentSearch}
                    onChange={(e) => handleEquipmentSearch(e.target.value)}
                    placeholder="Buscar por marca, modelo, part number..."
                    className="w-full pl-10 pr-10 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                  />
                  {equipmentSearch && (
                    <button
                      onClick={() => {
                        setEquipmentSearch('');
                        setShowEquipmentResults(false);
                      }}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
                      title="Limpiar búsqueda"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                  
                  {showEquipmentResults && (
                    <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-y-auto">
                      {filteredEquipment.length > 0 ? (
                        filteredEquipment.map(equip => (
                          <button
                            key={equip.id}
                            onClick={() => addCatalogEquipment(equip)}
                            className="w-full text-left px-4 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 border-b border-gray-100 dark:border-gray-700 last:border-b-0"
                          >
                            <div className="font-medium text-gray-900 dark:text-white">
                              {equip.tipo} {(() => {
                                const brand = brands.find(b => b.id === equip.marcaId);
                                return brand ? brand.nombre : 'Sin marca';
                              })()} {equip.modelo}
                            </div>
                            <div className="text-sm text-gray-600 dark:text-gray-400">P/N: {equip.numeroParte}</div>
                          </button>
                        ))
                      ) : equipmentSearch.trim() ? (
                        <div className="px-4 py-2 text-gray-500 dark:text-gray-400 text-center">
                          <div>No se encontraron equipos</div>
                          <div className="mt-2 space-y-1">
                            <button
                              onClick={() => {
                                setShowEquipmentForm(true);
                                setEquipmentSearch('');
                                setShowEquipmentResults(false);
                              }}
                              className="block w-full text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 text-sm"
                            >
                              + Crear Equipo de Catálogo
                            </button>
                            <button
                              onClick={() => {
                                setShowExemptForm(true);
                                setEquipmentSearch('');
                                setShowEquipmentResults(false);
                              }}
                              className="block w-full text-orange-600 dark:text-orange-400 hover:text-orange-800 dark:hover:text-orange-300 text-sm"
                            >
                              + Crear Equipo Exento
                            </button>
                          </div>
                        </div>
                      ) : null}
                    </div>
                  )}
                </div>
              </div>

              {/* Exempt Equipment Form */}
              {showExemptForm && (
                <div className="border border-orange-200 dark:border-orange-700 rounded-lg p-4 bg-orange-50 dark:bg-orange-900/20">
                  <h4 className="font-medium text-orange-800 dark:text-orange-300 mb-3">Crear Equipo Exento</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Descripción *
                      </label>
                      <input
                        type="text"
                        value={newExemptEquipment.descripcion}
                        onChange={(e) => setNewExemptEquipment(prev => ({ ...prev, descripcion: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 dark:focus:ring-orange-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                        placeholder="Descripción del equipo"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Serie
                      </label>
                      <input
                        type="text"
                        value={newExemptEquipment.series}
                        onChange={(e) => setNewExemptEquipment(prev => ({ ...prev, series: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 dark:focus:ring-orange-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                        placeholder="Número de serie"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Notas
                      </label>
                      <input
                        type="text"
                        value={newExemptEquipment.notes}
                        onChange={(e) => setNewExemptEquipment(prev => ({ ...prev, notes: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 dark:focus:ring-orange-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                        placeholder="Notas adicionales"
                      />
                    </div>
                  </div>
                  <div className="mt-3 flex space-x-2">
                    <button
                      onClick={addExemptEquipment}
                      disabled={!newExemptEquipment.descripcion.trim()}
                      className="px-3 py-1 bg-orange-600 text-white rounded-md hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                    >
                      Agregar
                    </button>
                    <button
                      onClick={() => {
                        setShowExemptForm(false);
                        setNewExemptEquipment({ descripcion: '', series: '', notes: '' });
                      }}
                      className="px-3 py-1 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 text-sm"
                    >
                      Cancelar
                    </button>
                  </div>
                </div>
              )}

              {/* Equipment List */}
              <div>
                <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-3">Equipos Agregados</h4>
                {formData.equipos.length > 0 ? (
                  <div className="space-y-3">
                    {formData.equipos.map((equip) => (
                      <div key={equip.id} className="border border-gray-200 dark:border-gray-600 rounded-lg p-4 bg-white dark:bg-gray-700">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-2">
                              <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
                                equip.type === 'catalog' 
                                  ? 'bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-400' 
                                  : 'bg-orange-100 dark:bg-orange-900/20 text-orange-800 dark:text-orange-400'
                              }`}>
                                {equip.type === 'catalog' ? 'Catálogo' : 'Exento'}
                              </span>
                            </div>
                            <h5 className="font-medium text-gray-900 dark:text-white">
                              {getEquipmentDisplayName(equip)}
                            </h5>
                            <div className="mt-3 space-y-3">
                              {/* Standard fields */}
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Serie
                                  </label>
                                  <input
                                    type="text"
                                    value={equip.series || ''}
                                    onChange={(e) => updateEquipmentField(equip.id, 'series', e.target.value)}
                                    className="w-full px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-gray-600 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 text-sm"
                                    placeholder="Número de serie"
                                  />
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Notas
                                  </label>
                                  <input
                                    type="text"
                                    value={equip.notes || ''}
                                    onChange={(e) => updateEquipmentField(equip.id, 'notes', e.target.value)}
                                    className="w-full px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-gray-600 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 text-sm"
                                    placeholder="Notas adicionales"
                                  />
                                </div>
                              </div>

                              {/* Customizable specifications */}
                              {(() => {
                                const customizableSpecs = getCustomizableSpecs(equip);
                                if (customizableSpecs.length === 0) return null;

                                return (
                                  <div>
                                    <h6 className="text-sm font-medium text-gray-900 dark:text-white mb-2 flex items-center">
                                      <Wrench className="h-4 w-4 mr-1 text-blue-600 dark:text-blue-400" />
                                      Especificaciones Personalizables
                                    </h6>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                      {customizableSpecs.map((spec) => (
                                        <div key={spec.name}>
                                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                            {spec.name}
                                            <span className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-400">
                                              Personalizable
                                            </span>
                                          </label>
                                          <input
                                            type="text"
                                            value={(equip.customSpecs && equip.customSpecs[spec.name]) || ''}
                                            onChange={(e) => updateEquipmentCustomSpec(equip.id, spec.name, e.target.value)}
                                            className="w-full px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-gray-600 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 text-sm"
                                            placeholder={`Por defecto: ${spec.defaultValue || 'No especificado'}`}
                                          />
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                );
                              })()}
                            </div>
                          </div>
                          <button
                            onClick={() => removeEquipment(equip.id)}
                            className="ml-4 p-1 text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-gray-500 dark:text-gray-400 text-center py-8">
                    No hay equipos agregados
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Technicians Tab */}
          {activeTab === 'technicians' && (
            <div className="space-y-6">
              <div>
                <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-3">Asignar Técnicos</h4>
                {technicians.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {technicians.map((tech) => (
                      <div
                        key={tech.id}
                        className={`border rounded-lg p-3 cursor-pointer transition-colors ${
                          formData.tecnicos.includes(tech.id)
                            ? 'border-blue-500 dark:border-blue-400 bg-blue-50 dark:bg-blue-900/30'
                            : 'border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                        }`}
                        onClick={() => toggleTechnician(tech.id)}
                      >
                        <div className="flex items-center space-x-3">
                          <input
                            type="checkbox"
                            checked={formData.tecnicos.includes(tech.id)}
                            onChange={() => toggleTechnician(tech.id)}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <div>
                            <div className="font-medium text-gray-900 dark:text-white">{tech.nombre}</div>
                            {tech.telefono && (
                              <div className="text-sm text-gray-600 dark:text-gray-400">{tech.telefono}</div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-gray-500 dark:text-gray-400 text-center py-8">
                    No hay técnicos disponibles
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Service Details Tab */}
          {activeTab === 'service' && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Observaciones y Detalles
                </label>
                <textarea
                  value={formData.observaciones_detalle}
                  onChange={(e) => handleInputChange('observaciones_detalle', e.target.value)}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                  placeholder="Describe los detalles del servicio..."
                />
              </div>


              {['Reparación', 'Garantía'].includes(formData.tipo_de_orden) && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Comentarios del Cliente
                    </label>
                    <textarea
                      value={formData.comentarios_cliente}
                      onChange={(e) => handleInputChange('comentarios_cliente', e.target.value)}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                      placeholder="Comentarios o quejas del cliente..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Resultados de Revisión
                    </label>
                    <textarea
                      value={formData.resultados_revision}
                      onChange={(e) => handleInputChange('resultados_revision', e.target.value)}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                      placeholder="Resultados de la revisión técnica..."
                    />
                  </div>
                </div>
              )}

              {formData.tipo_de_orden === 'Garantía' && (
                <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                  <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Información de Garantía</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Fecha de Compra del Equipo
                      </label>
                      <input
                        type="date"
                        value={formData.fecha_compra_equipo}
                        onChange={(e) => handleInputChange('fecha_compra_equipo', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Folio de Compra
                      </label>
                      <input
                        type="text"
                        value={formData.folio_compra}
                        onChange={(e) => handleInputChange('folio_compra', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                        placeholder="Folio de la compra original"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        RMA
                      </label>
                      <input
                        type="text"
                        value={formData.rma}
                        onChange={(e) => handleInputChange('rma', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                        placeholder="Número de RMA"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Veredicto
                      </label>
                      <select
                        value={formData.veredicto}
                        onChange={(e) => handleInputChange('veredicto', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                      >
                        <option value="">Seleccionar veredicto</option>
                        <option value="Garantía aprobada">Garantía aprobada</option>
                        <option value="No procede garantía">No procede garantía</option>
                        <option value="Sin falla detectada">Sin falla detectada</option>
                      </select>
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Detalle del Veredicto
                      </label>
                      <textarea
                        value={formData.detalle_veredicto}
                        onChange={(e) => handleInputChange('detalle_veredicto', e.target.value)}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                        placeholder="Detalles adicionales del veredicto..."
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Financial Information Tab */}
          {activeTab === 'financial' && ['Reparación', 'Ensamble', 'Garantía'].includes(formData.tipo_de_orden) && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Folio de Cotización
                  </label>
                  <input
                    type="text"
                    value={formData.folio_cotizacion}
                    onChange={(e) => handleInputChange('folio_cotizacion', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                    placeholder="Folio de la cotización"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Importe
                  </label>
                  <input
                    type="text"
                    value={formData.importe || ''}
                    onChange={(e) => handleImporteChange(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                    placeholder="0.00"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Estado de Autorización
                  </label>
                  <select
                    value={formData.autorizacion}
                    onChange={(e) => handleInputChange('autorizacion', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                  >
                    <option value="">Seleccionar estado</option>
                    <option value="Autorizado">Autorizado</option>
                    <option value="No autorizado">No autorizado</option>
                    <option value="No aplica">No aplica</option>
                  </select>
                </div>

                {formData.autorizacion === 'Autorizado' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Fecha de Autorización *
                    </label>
                    <input
                      type="date"
                      value={formData.fecha_autorizacion}
                      onChange={(e) => handleInputChange('fecha_autorizacion', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white ${
                        errors.fecha_autorizacion ? 'border-red-300 dark:border-red-600' : 'border-gray-300 dark:border-gray-600'
                      }`}
                      required
                    />
                    {errors.fecha_autorizacion && (
                      <p className="mt-1 text-sm text-red-600">{errors.fecha_autorizacion}</p>
                    )}
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Estado de Pago
                  </label>
                  <select
                    value={formData.estatus_pago}
                    onChange={(e) => handleInputChange('estatus_pago', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                  >
                    <option value="">Seleccionar estado</option>
                    <option value="Sin cargo">Sin cargo</option>
                    <option value="Esperando pago">Esperando pago</option>
                    <option value="Pagado">Pagado</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Folio de Factura
                  </label>
                  <input
                    type="text"
                    value={formData.folio_factura_contpaq}
                    onChange={(e) => handleInputChange('folio_factura_contpaq', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                    placeholder="Folio de factura ContPaq"
                  />
                </div>

                {formData.tipo_de_orden === 'Ensamble' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Folio de Orden de Compra
                      </label>
                      <input
                        type="text"
                        value={formData.folio_orden_compra}
                        onChange={(e) => handleInputChange('folio_orden_compra', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                        placeholder="Folio de orden de compra"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Folio de Salida ContPaq
                      </label>
                      <input
                        type="text"
                        value={formData.folio_salida_contpaq}
                        onChange={(e) => handleInputChange('folio_salida_contpaq', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                        placeholder="Folio de salida ContPaq"
                      />
                    </div>
                  </>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Fecha de Conclusión del Servicio
                  </label>
                  <input
                    type="date"
                    value={formData.fecha_conclusion_servicio}
                    onChange={(e) => handleInputChange('fecha_conclusion_servicio', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Fecha de Entrega
                  </label>
                  <input
                    type="date"
                    value={formData.fecha_entrega_equipo}
                    onChange={(e) => handleInputChange('fecha_entrega_equipo', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Quien Recibe
                  </label>
                  <select
                    value={formData.quien_recibe_equipo}
                    onChange={(e) => handleInputChange('quien_recibe_equipo', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                  >
                    <option value="">Seleccionar...</option>
                    <option value="Cliente">Cliente</option>
                    <option value="Mostrador de ventas">Mostrador de ventas</option>
                    <option value="Envío por paquetería">Envío por paquetería</option>
                    <option value="Recolección">Recolección</option>
                    <option value="Reciclaje">Reciclaje</option>
                  </select>
                </div>
              </div>

            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              * Solo folio y fecha son requeridos
            </div>
            <div className="flex space-x-3">
              <button
                onClick={onClose}
                disabled={loading}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 dark:focus:ring-gray-400 disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                onClick={() => handleSave(true)}
                disabled={loading}
                className="px-4 py-2 bg-blue-600 dark:bg-blue-500 text-white rounded-md hover:bg-blue-700 dark:hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 disabled:opacity-50"
              >
                {loading ? 'Guardando...' : 'Guardar y Cerrar'}
              </button>
            </div>
          </div>
        </div>
      </div>
      </div>

      {/* Client Form Modal */}
      <ClientForm
        isOpen={showClientForm}
        onClose={handleClientFormClose}
        onSuccess={handleClientFormSuccess}
        editingClient={null}
      />

      {/* Equipment Form Modal */}
      <EquipmentForm
        isOpen={showEquipmentForm}
        onClose={handleCloseEquipmentForm}
        onSuccess={handleEquipmentFormSuccess}
        editingEquipment={null}
      />
    </>
  );
};

export default OrderForm;